import { useState, useEffect, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, List, ListOrdered, Undo, Redo, Check, X } from "lucide-react";
import { useEdit } from "@/components/EditContext";

function MenuBar({ editor, onSave, onCancel }) {
  if (!editor) return null;

  return (
    <div className="flex items-center gap-1 p-2 bg-w-cream border-b border-w-sand">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`p-1.5 rounded transition-colors ${editor.isActive('bold') ? 'bg-w-sage text-white' : 'hover:bg-w-sand/50'}`}
        title="Grassetto"
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`p-1.5 rounded transition-colors ${editor.isActive('italic') ? 'bg-w-sage text-white' : 'hover:bg-w-sand/50'}`}
        title="Corsivo"
      >
        <Italic className="w-4 h-4" />
      </button>
      <div className="w-px h-5 bg-w-sand mx-1" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`p-1.5 rounded transition-colors ${editor.isActive('bulletList') ? 'bg-w-sage text-white' : 'hover:bg-w-sand/50'}`}
        title="Elenco puntato"
      >
        <List className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`p-1.5 rounded transition-colors ${editor.isActive('orderedList') ? 'bg-w-sage text-white' : 'hover:bg-w-sand/50'}`}
        title="Elenco numerato"
      >
        <ListOrdered className="w-4 h-4" />
      </button>
      <div className="w-px h-5 bg-w-sand mx-1" />
      <button
        type="button"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className="p-1.5 rounded hover:bg-w-sand/50 disabled:opacity-30"
        title="Annulla"
      >
        <Undo className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className="p-1.5 rounded hover:bg-w-sand/50 disabled:opacity-30"
        title="Ripeti"
      >
        <Redo className="w-4 h-4" />
      </button>
      <div className="flex-1" />
      <button
        type="button"
        onClick={onCancel}
        className="p-1.5 rounded hover:bg-red-100 text-red-600"
        title="Annulla modifiche"
      >
        <X className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={onSave}
        className="p-1.5 rounded hover:bg-green-100 text-green-600"
        title="Salva"
      >
        <Check className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function EditableText({ id, children, tag: Tag = "span", className = "" }) {
  const { isEditing, content, saveContent } = useEdit();
  const [isOpen, setIsOpen] = useState(false);
  const [originalContent, setOriginalContent] = useState("");
  
  const text = content[id] !== undefined ? content[id] : children;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: "Scrivi qui...",
      }),
    ],
    content: text,
    editable: true,
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[60px] p-3",
      },
    },
  });

  useEffect(() => {
    if (editor && text && !isOpen) {
      editor.commands.setContent(text);
    }
  }, [text, editor, isOpen]);

  const handleOpen = useCallback(() => {
    if (!isEditing) return;
    setOriginalContent(text);
    setIsOpen(true);
  }, [isEditing, text]);

  const handleSave = useCallback(() => {
    if (editor) {
      const html = editor.getHTML();
      const plainText = editor.getText();
      // Salva HTML se contiene formattazione, altrimenti testo puro
      const hasFormatting = html.includes('<strong>') || html.includes('<em>') || html.includes('<ul>') || html.includes('<ol>');
      saveContent(id, hasFormatting ? html : plainText);
    }
    setIsOpen(false);
  }, [editor, id, saveContent]);

  const handleCancel = useCallback(() => {
    if (editor) {
      editor.commands.setContent(originalContent);
    }
    setIsOpen(false);
  }, [editor, originalContent]);

  // Render senza modalita' editing
  if (!isEditing) {
    // Se il contenuto e' HTML, renderizzalo come tale
    if (typeof text === 'string' && text.startsWith('<')) {
      return <Tag className={className} dangerouslySetInnerHTML={{ __html: text }} />;
    }
    return <Tag className={className}>{text}</Tag>;
  }

  // Render in modalita' editing ma non ancora aperto l'editor
  if (!isOpen) {
    return (
      <Tag
        onClick={handleOpen}
        className={`${className} cursor-pointer ring-1 ring-transparent hover:ring-w-sage/50 transition-shadow rounded-sm relative group`}
        data-testid={`editable-${id}`}
      >
        {typeof text === 'string' && text.startsWith('<') ? (
          <span dangerouslySetInnerHTML={{ __html: text }} />
        ) : (
          text
        )}
        <span className="absolute -top-6 left-0 text-[10px] font-body uppercase tracking-wider text-w-sage opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1 rounded">
          Clicca per modificare
        </span>
      </Tag>
    );
  }

  // Editor WYSIWYG aperto
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-w-charcoal/50 backdrop-blur-sm" data-testid={`wysiwyg-${id}`}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4 overflow-hidden border border-w-sand">
        <div className="bg-w-sage text-white px-4 py-2 font-body text-sm">
          Modifica testo
        </div>
        <MenuBar editor={editor} onSave={handleSave} onCancel={handleCancel} />
        <div className="max-h-[300px] overflow-y-auto">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
}
